import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGet, apiPost } from "../lib/api";
import { COLORS } from "../lib/colors";

export default function SetupPage({ onComplete }) {
  const { session } = useAuth();
  const token = session?.access_token;

  const [tab, setTab] = useState("select"); // "select" | "create"
  const [institutions, setInstitutions] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [selectedInst, setSelectedInst] = useState("");
  const [selectedProg, setSelectedProg] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Create form (institution)
  const [newName, setNewName] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newWebsite, setNewWebsite] = useState("");

  // Create form (programme)
  const [showProgCreate, setShowProgCreate] = useState(false);
  const [newProgName, setNewProgName] = useState("");
  const [newProgDegree, setNewProgDegree] = useState("");
  const [newProgUrl, setNewProgUrl] = useState("");

  useEffect(() => {
    if (token) loadInstitutions();
  }, [token]);

  useEffect(() => {
    if (selectedInst && token) loadProgrammes(selectedInst);
  }, [selectedInst, token]);

  const loadInstitutions = async () => {
    try {
      const data = await apiGet("/institutions", token);
      setInstitutions(Array.isArray(data) ? data : data.institutions || []);
    } catch (err) {
      setError("Failed to load institutions");
    }
  };

  const loadProgrammes = async (instId) => {
    try {
      const data = await apiGet(`/programmes?institution_id=${instId}`, token);
      setProgrammes(Array.isArray(data) ? data : data.programmes || []);
    } catch {
      setProgrammes([]);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await apiPost("/institutions", {
        name: newName, country: newCountry || "Unknown", city: newCity || null, website: newWebsite || null,
      }, token);
      await loadInstitutions();
      setSelectedInst(data.institution?.id || data.id);
      setTab("select");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProgramme = async (e) => {
    e.preventDefault();
    if (!selectedInst || !newProgName) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiPost("/programmes", {
        institution_id: selectedInst,
        programme_name: newProgName,
        degree_type: newProgDegree || null,
        primary_url: newProgUrl || null,
      }, token);
      await loadProgrammes(selectedInst);
      const prog = data.programme;
      setSelectedProg(prog?.id || "");
      setShowProgCreate(false);
      setNewProgName("");
      setNewProgDegree("");
      setNewProgUrl("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (!selectedInst) return;
    const inst = institutions.find(i => i.id === selectedInst);
    onComplete(selectedInst, selectedProg || null, inst?.name || "");
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", fontSize: 14,
    border: `1px solid ${COLORS.border}`, borderRadius: 8,
    outline: "none", boxSizing: "border-box",
    backgroundColor: COLORS.surfaceAlt, color: COLORS.text,
  };

  const labelStyle = {
    fontSize: 13, fontWeight: 500, color: COLORS.textSecondary,
    display: "block", marginBottom: 6,
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Setup</h2>
      <p style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
        Select or create an institution and programme to begin.
      </p>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {["select", "create"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "8px 20px", fontSize: 13, fontWeight: 500, borderRadius: 8,
              border: `1px solid ${tab === t ? COLORS.accent : COLORS.border}`,
              backgroundColor: tab === t ? COLORS.accentLight : COLORS.surface,
              color: tab === t ? COLORS.accent : COLORS.textSecondary,
              cursor: "pointer",
            }}
          >{t === "select" ? "Select Existing" : "Create New"}</button>
        ))}
      </div>

      {error && (
        <div style={{
          backgroundColor: COLORS.redLight, color: COLORS.red,
          padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 16,
        }}>{error}</div>
      )}

      {tab === "select" ? (
        <div style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 24,
        }}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Institution</label>
            <select
              value={selectedInst}
              onChange={(e) => setSelectedInst(e.target.value)}
              style={inputStyle}
            >
              <option value="">-- Select institution --</option>
              {institutions.map((inst) => (
                <option key={inst.id} value={inst.id}>
                  {inst.name} {inst.city && `(${inst.city}, ${inst.country})`}
                </option>
              ))}
            </select>
          </div>

          {selectedInst && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Programme (optional)</label>
              <select
                value={selectedProg}
                onChange={(e) => setSelectedProg(e.target.value)}
                style={inputStyle}
              >
                <option value="">-- Select programme --</option>
                {programmes.map((prog) => (
                  <option key={prog.id} value={prog.id}>
                    {prog.programme_name || prog.name}
                  </option>
                ))}
              </select>

              {!showProgCreate ? (
                <button
                  onClick={() => setShowProgCreate(true)}
                  style={{
                    marginTop: 8, padding: "6px 14px", fontSize: 13,
                    backgroundColor: COLORS.surfaceAlt, color: COLORS.textSecondary,
                    border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer",
                  }}
                >+ Create Programme</button>
              ) : (
                <div style={{
                  marginTop: 12, padding: 16, borderRadius: 8,
                  border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surfaceAlt,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>New Programme</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={labelStyle}>Programme Name *</label>
                      <input value={newProgName} onChange={(e) => setNewProgName(e.target.value)} required style={inputStyle} placeholder="MBA International Business" />
                    </div>
                    <div>
                      <label style={labelStyle}>Degree Type</label>
                      <input value={newProgDegree} onChange={(e) => setNewProgDegree(e.target.value)} style={inputStyle} placeholder="MBA / MSc / BBA" />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>Programme URL</label>
                    <input value={newProgUrl} onChange={(e) => setNewProgUrl(e.target.value)} style={inputStyle} placeholder="https://www.university.edu/mba" />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={handleCreateProgramme}
                      disabled={loading || !newProgName}
                      style={{
                        padding: "8px 16px", fontSize: 13, fontWeight: 600,
                        backgroundColor: COLORS.accent, color: "#fff",
                        border: "none", borderRadius: 6,
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.7 : 1,
                      }}
                    >{loading ? "Creating..." : "Create"}</button>
                    <button
                      onClick={() => setShowProgCreate(false)}
                      style={{
                        padding: "8px 16px", fontSize: 13,
                        backgroundColor: COLORS.surfaceAlt, color: COLORS.textSecondary,
                        border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer",
                      }}
                    >Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleSelect}
            disabled={!selectedInst}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              backgroundColor: selectedInst ? COLORS.accent : COLORS.border,
              color: selectedInst ? "#fff" : COLORS.textMuted,
              border: "none", borderRadius: 8, cursor: selectedInst ? "pointer" : "not-allowed",
            }}
          >Continue</button>
        </div>
      ) : (
        <form onSubmit={handleCreate} style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 24,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Institution Name *</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} required style={inputStyle} placeholder="University of Vaasa" />
            </div>
            <div>
              <label style={labelStyle}>Country</label>
              <input value={newCountry} onChange={(e) => setNewCountry(e.target.value)} style={inputStyle} placeholder="Finland" />
            </div>
            <div>
              <label style={labelStyle}>City</label>
              <input value={newCity} onChange={(e) => setNewCity(e.target.value)} style={inputStyle} placeholder="Vaasa" />
            </div>
            <div>
              <label style={labelStyle}>Website</label>
              <input value={newWebsite} onChange={(e) => setNewWebsite(e.target.value)} style={inputStyle} placeholder="https://www.uwasa.fi" />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !newName}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              backgroundColor: COLORS.accent, color: "#fff",
              border: "none", borderRadius: 8,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >{loading ? "Creating..." : "Create Institution"}</button>
        </form>
      )}
    </div>
  );
}
